// Org role resolution — single source of truth for "what roles does the current
// user effectively have in this org" and "may they perform org-admin actions".
//
// Background (GH#181): a T3Admin/superAdmin who "takes control" of a customer
// org usually isn't in that org's `users` map, so they have no member roles.
// While they hold an active control session for the org we grant an effective
// "admin" role so the org-admin-gated UI (enter transactions, reconcile, edit
// contacts/donors/funds/labels) is usable while they support the org. Any real
// (non-admin) member roles they happen to have are preserved.

/**
 * Compute the user's effective roles for an org.
 *
 * Pure function — derived from orgData + controlSessions so the result is
 * correct regardless of which Firestore listener resolves first, and clears the
 * moment control is released (controlSessions no longer contains the org) or the
 * org changes (orgData becomes null).
 *
 * @param {object}   params
 * @param {object|null} params.orgData         The org document (contains `users`).
 * @param {Array}    [params.controlSessions]  Active admin control sessions.
 * @param {string}   params.org                Current org id.
 * @param {string}   params.uid                Current user's uid.
 * @returns {string[]} Effective role strings (e.g. ["admin"], ["bookkeeper"]).
 */
export function computeEffectiveOrgRoles({ orgData, controlSessions, org, uid }) {
  if (!org || !uid || !orgData) return [];

  const baseRoles = orgData.users?.[uid]?.roles ?? [];
  const hasControlSession = (controlSessions ?? []).some(
    (s) => s.orgId === org,
  );

  if (hasControlSession && !baseRoles.includes("admin")) {
    return [...baseRoles, "admin"];
  }
  return baseRoles;
}

/**
 * Whether the user may perform org-admin actions (enter transactions, reconcile,
 * edit org data). This is the single gate used by the JE / credit-card /
 * bank-account entry forms.
 *
 * @param {object}   params
 * @param {string[]} [params.orgRoles]   Effective org roles (see computeEffectiveOrgRoles).
 * @param {boolean}  [params.superAdmin] Whether the user is a platform super admin.
 * @returns {boolean}
 */
export function canManageOrg({ orgRoles, superAdmin } = {}) {
  return (
    (orgRoles ?? []).some((role) => role === "admin" || role === "bookkeeper") ||
    Boolean(superAdmin)
  );
}
